<script setup lang="tsx">
import { ref, onBeforeMount, computed } from 'vue'
import http from '@/services/http'
import type { ResponsibleCostResponse, Response, Responsible } from '@/models/api-generated'
import {
	ConditionallyImplemented,
	Implemented,
	NeedsAttention,
	NotImplemented,
	PartiallyImplemented,
	WillNotApply,
	WillNotImplement,
	PermOwnMeasureRead,
	PermAllMeasureRead,
	DueDateOverdue,
} from '@/models/enums-generated'
import { getResponsibleIcon, hasPermission, getServiceProviderIcon, generateDueDateParams } from '@/services/utils'
import { useI18n } from 'vue-i18n'
import OrderLabel from '../widgets/OrderLabel.vue'
import columnStore from '@/stores/column'
import useDashboardColumns from './columns'

const props = defineProps({
	component: { default: 'dashboard' },
})

const { columns } = useDashboardColumns()

const root = columnStore().defineComponent(props.component, async () => {
	await fetchData()
})

const { t } = useI18n()
const result = ref<ResponsibleCostResponse>()
const daysBack = ref(30)
const currentUser = sessionMap.user
const canViewMethods = computed(() => hasPermission(PermOwnMeasureRead, PermAllMeasureRead))
const expanded = ref(false)

function toggleExpand() {
	expanded.value = !expanded.value
}

async function fetchData() {
	if (!canViewMethods.value) {
		result.value = {
			responsibles: [],
			serviceProviders: [],
			responsibleSummary: {
				total: { count: 0, percentage: 0 },
				statuses: [],
				price: 0,
				changes: 0,
				isServiceProvider: false,
				responsible: {} as Responsible,
				overdueDates: 0,
			},
			serviceProviderSummary: {
				total: { count: 0, percentage: 0 },
				statuses: [],
				price: 0,
				changes: 0,
				isServiceProvider: true,
				responsible: {} as Responsible,
				overdueDates: 0,
			},
		} as ResponsibleCostResponse
		return
	}

	const orderBy = root.orderValue()
	const orderField = orderBy?.field ?? 'price'
	const orderDir = orderBy?.order ?? 'desc'

	const { data } = await http.get<Response<ResponsibleCostResponse>>(
		`api/dashboard/responsibleCostMethods?days=${daysBack.value}&orderField=${orderField}&orderDir=${orderDir}`,
	)

	result.value = data.data
}

onBeforeMount(fetchData)

function getStatusClass(status: string | undefined) {
	switch (status) {
		case Implemented:
			return 'series-i'
		case PartiallyImplemented:
			return 'series-pi'
		case NotImplemented:
			return 'series-ni'
		case WillNotApply:
			return 'series-wna'
		case NeedsAttention:
			return 'series-na'
		case WillNotImplement:
			return 'series-wni stripe-effect'
		case ConditionallyImplemented:
			return 'series-ci stripe-effect'
	}
	return ''
}

function goToMethodsPage(status: string, user?: Responsible, serviceProviders?: Responsible[]) {
	const params = new URLSearchParams()

	if (status) {
		params.append('status', status)
	}

	if (user) {
		params.append('responsible', JSON.stringify({ id: user.id, type: user.type }))
	}

	if (serviceProviders?.length) {
		params.set('serviceProvider', JSON.stringify(serviceProviders.map((sp) => ({ id: sp.id, type: sp.type }))))
	}

	window.location.href = `/app/methods?${params}`
}

function goToOverdueMethods(user?: Responsible, serviceProviders?: Responsible[]) {
	const params = generateDueDateParams(DueDateOverdue)

	if (user) {
		params.append('responsible', JSON.stringify({ id: user.id, type: user.type }))
	}

	if (serviceProviders?.length) {
		params.set('serviceProvider', JSON.stringify(serviceProviders.map((sp) => ({ id: sp.id, type: sp.type }))))
	}

	params.append('status', NotImplemented)
	params.append('status', NeedsAttention)
	params.append('status', PartiallyImplemented)
	params.append('status', WillNotApply)
	params.append('status', WillNotImplement)

	window.location.href = `/app/methods?${params}`
}
</script>

<template>
	<div class="d-flex align-items-center justify-content-between mb-2">
		<div class="d-flex align-items-center">
			<button class="btn btn-sm btn-outline-primary me-2" @click="toggleExpand">
				<i :class="expanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
			</button>

			<p class="fw-bold mb-0">
				{{ $t('responsibleCost.label') }}
				<template v-if="hasPermission(PermOwnMeasureRead) && !hasPermission(PermAllMeasureRead)">
					- {{ $t('user.label') }} {{ currentUser.firstname }} {{ currentUser.lastname }}
				</template>
				<template v-else-if="!canViewMethods"> - {{ $t('roles.missingRights') }} </template>
			</p>
		</div>
	</div>

	<div v-if="result" class="container">
		<div v-if="expanded" class="header-row">
			<div>
				<OrderLabel v-model="columns.responsible.value">{{ columns.responsible.value.label }}</OrderLabel>
			</div>
			<div>
				<OrderLabel v-model="columns.overdueDates.value">{{ columns.overdueDates.value.label }}</OrderLabel>
			</div>
			<div>
				<OrderLabel v-model="columns.methodCount.value">{{ columns.methodCount.value.label }}</OrderLabel>
			</div>
			<div>
				<OrderLabel v-model="columns.price.value">{{ columns.price.value.label }}</OrderLabel>
			</div>
			<div>
				<span v-one-tooltip="$t('responsibleCost.changesTooltip')" class="me-1">
					<OrderLabel v-model="columns.changeCount.value">{{ columns.changeCount.value.label }}</OrderLabel>
				</span>
				<label>
					<input type="number" v-model="daysBack" @change="fetchData" min="1" class="form-control small-input me-1" />
				</label>
			</div>
			<div>{{ t('status.label') }}</div>
		</div>

		<div v-if="result.responsibleSummary" class="summary-row">
			<div><i class="bi-people-fill me-2"></i>{{ t('responsibleCost.responsibles') }} {{ result.responsibles?.length || 0 }}</div>
			<div class="risks-cell" @click="goToOverdueMethods()" v-one-tooltip="$t('responsibleCost.goToOverdueMethods')">
				{{ result.responsibleSummary.overdueDates }}
			</div>
			<div>{{ result.responsibleSummary.total.count }}</div>
			<div>{{ result.responsibleSummary.price.toFixed(0) }} €</div>
			<div>{{ result.responsibleSummary.changes }}</div>
			<div>
				<div class="status-bar">
					<div
						v-for="(segment, sIndex) in result.responsibleSummary.statuses"
						:key="'r-summary-' + sIndex"
						:style="{ width: segment.data.percentage + '%' }"
					>
						<div
							:class="['status-segment', getStatusClass(segment.status)]"
							v-one-tooltip="
								`${segment.data.count} ${t('responsibleCost.method', segment.data.count)} (${segment.data.percentage.toFixed(2)}%) - ${segment.price.toFixed(2)} €`
							"
							@click="goToMethodsPage(segment.status, undefined, undefined)"
						>
							<span v-if="segment.data.percentage >= 5" class="segment-text">
								{{ segment.data.count }} ({{ segment.data.percentage.toFixed(0) }}%)
							</span>
							<span v-else>&nbsp;</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<template v-if="expanded && result.responsibles">
			<div v-for="(item, i) in result.responsibles" :key="'resp-' + i" class="data-row">
				<div>
					<span :class="getResponsibleIcon(item.responsible)"></span>
					{{ item.responsible.label || t('misc.nothing') }}
				</div>
				<div @click="goToOverdueMethods(item.responsible)" class="risks-cell" v-one-tooltip="$t('responsibleCost.goToOverdueMethods')">
					{{ item.overdueDates }}
				</div>
				<div
					@click="goToMethodsPage('', item.responsible, undefined)"
					class="risks-cell"
					v-one-tooltip="$t('responsibleCost.goToMethodsPage')"
				>
					{{ item.total.count }} ({{ item.total.percentage.toFixed(2) }}%)
				</div>
				<div>{{ item.price.toFixed(0) }} €</div>
				<div>{{ item.changes }}</div>
				<div>
					<div class="status-bar">
						<div v-for="(segment, sIndex) in item.statuses" :key="sIndex" :style="{ width: segment.data.percentage + '%' }">
							<div
								v-one-tooltip="
									`${segment.data.count} ${t('responsibleCost.method', segment.data.count)} (${segment.data.percentage.toFixed(2)}%) - ${segment.price.toFixed(2)} €`
								"
								:class="['status-segment', getStatusClass(segment.status)]"
								@click="goToMethodsPage(segment.status, item.responsible, undefined)"
							>
								<span v-if="segment.data.percentage >= 5" class="segment-text">
									{{ segment.data.count }} ({{ segment.data.percentage.toFixed(0) }}%)
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>

		<div v-if="result.serviceProviderSummary" class="summary-row service-provider-summary">
			<div>
				<i class="bi-building-fill me-2 purple-icon"></i>{{ t('responsibleCost.serviceProviders') }}
				{{ result.serviceProviders?.length || 0 }}
			</div>
			<div
				@click="
					goToOverdueMethods(
						undefined,
						result.serviceProviders.map((sp) => sp.responsible),
					)
				"
				class="risks-cell"
				v-one-tooltip="$t('responsibleCost.goToOverdueMethods')"
			>
				{{ result.serviceProviderSummary.overdueDates }}
			</div>
			<div
				@click="
					goToMethodsPage(
						'',
						undefined,
						result.serviceProviders.map((sp) => sp.responsible),
					)
				"
				class="risks-cell"
				v-one-tooltip="$t('responsibleCost.goToMethodsPage')"
			>
				{{ result.serviceProviderSummary.total.count }}
			</div>
			<div>{{ result.serviceProviderSummary.price.toFixed(0) }} €</div>
			<div>{{ result.serviceProviderSummary.changes }}</div>
			<div>
				<div class="status-bar">
					<div
						v-for="(segment, sIndex) in result.serviceProviderSummary.statuses"
						:key="'sp-summary-' + sIndex"
						:style="{ width: segment.data.percentage + '%' }"
					>
						<div
							:class="['status-segment', getStatusClass(segment.status)]"
							v-one-tooltip="
								`${segment.data.count} ${t('responsibleCost.method', segment.data.count)} (${segment.data.percentage.toFixed(2)}%) - ${segment.price.toFixed(2)} €`
							"
							@click="
								goToMethodsPage(
									segment.status,
									undefined,
									result.serviceProviders.map((sp) => sp.responsible),
								)
							"
						>
							<span v-if="segment.data.percentage >= 5" class="segment-text">
								{{ segment.data.count }} ({{ segment.data.percentage.toFixed(0) }}%)
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<template v-if="expanded && result.serviceProviders">
			<div v-for="(item, i) in result.serviceProviders" :key="'sp-' + i" class="data-row">
				<div>
					<i :class="getServiceProviderIcon(item.responsible)"></i>
					{{ item.responsible.label || t('misc.nothing') }}
				</div>
				<div
					@click="goToOverdueMethods(undefined, [item.responsible])"
					class="risks-cell"
					v-one-tooltip="$t('responsibleCost.goToOverdueMethods')"
				>
					{{ item.overdueDates }}
				</div>
				<div
					@click="goToMethodsPage('', undefined, [item.responsible])"
					class="risks-cell"
					v-one-tooltip="$t('responsibleCost.goToMethodsPage')"
				>
					{{ item.total.count }} ({{ item.total.percentage.toFixed(2) }}%)
				</div>
				<div>{{ item.price.toFixed(0) }} €</div>
				<div>{{ item.changes }}</div>
				<div>
					<div class="status-bar">
						<div v-for="(segment, sIndex) in item.statuses" :key="sIndex" :style="{ width: segment.data.percentage + '%' }">
							<div
								v-one-tooltip="
									`${segment.data.count} ${t('responsibleCost.method', segment.data.count)} (${segment.data.percentage.toFixed(2)}%) - ${segment.price.toFixed(2)} €`
								"
								:class="['status-segment', getStatusClass(segment.status)]"
								@click="goToMethodsPage(segment.status, undefined, [item.responsible])"
							>
								<span v-if="segment.data.percentage >= 5" class="segment-text">
									{{ segment.data.count }} ({{ segment.data.percentage.toFixed(0) }}%)
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</div>

	<div v-if="!result" class="p-4 text-center">
		<div class="spinner-border text-primary"></div>
	</div>
</template>

<style lang="sass" scoped>
.header-row
  display: grid
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 5fr
  align-items: center

.summary-row
  display: grid
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 5fr
  align-items: center
  padding: 8px 0
  border-bottom: 2px solid #bbb
  color: #6c757d

.service-provider-summary
  border-top: 2px solid #bbb
  color: #6f42c1
  font-weight: 600

.data-row
  display: grid
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 5fr
  align-items: center
  padding: 8px 0
  border-bottom: 1px solid #eee
  &:hover
    background: var(--bs-primary-bg-subtle)

.service-provider-row
  color: #5a189a

.purple-icon
  color: #6f42c1

.status-bar
  display: flex
  height: 22px
  border-radius: 4px
  overflow: hidden
  cursor: pointer

.status-segment
  display: flex
  justify-content: center
  align-items: center
  font-size: 10px
  color: black
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis
  height: 100%

.segment-text
  position: relative
  z-index: 2

.status-segment.stripe-effect .segment-text
  padding: 1px 3px
  backdrop-filter: blur(2px)

.small-input
  width: 45px
  padding: 2px 6px
  font-size: 0.7rem
</style>
